// ============================================================================
// CrewAhead Maritime Platform - Payments API Functions
// ============================================================================
// Subscription plans, subscriptions, invoices, wallet, and transactions.

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PriceDisplay {
  currency: string;
  amount: string;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  planType: string;
  description: string;
  priceUsd: string;
  priceDisplay: PriceDisplay;
  billingCycle: string;
  features: Record<string, unknown>;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
}

export interface Subscription {
  id: number;
  plan: SubscriptionPlan;
  status: 'active' | 'trial' | 'expired' | 'cancelled' | 'past_due';
  currency: string;
  amountPaid: string;
  startDate: string;
  endDate: string;
  trialEndDate: string | null;
  autoRenew: boolean;
  gatewaySubscriptionId: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Payment {
  id: number;
  paymentType: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded' | 'partially_refunded' | 'cancelled';
  currency: string;
  amount: string;
  amountUsd: string;
  gateway: 'razorpay' | 'stripe' | 'manual' | 'credits';
  gatewayPaymentId: string | null;
  gatewayOrderId: string | null;
  subscription: number | null;
  description: string;
  notes: string;
  dataPayload: Record<string, unknown>;
  refundedAmount: string;
  refundReason: string;
  paidAt: string | null;
  failedAt: string | null;
  refundedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: number;
  invoiceNumber: string;
  payment: number;
  currency: string;
  subtotal: string;
  taxAmount: string;
  discountAmount: string;
  totalAmount: string;
  taxDetails: Record<string, unknown>;
  billingName: string;
  billingAddress: string;
  billingEmail: string;
  billingGstin: string;
  status: 'draft' | 'issued' | 'paid' | 'void';
  dueDate: string | null;
  paidAt: string | null;
  pdfFile: string | null;
  createdAt: string;
}

export interface Wallet {
  id: number;
  balance: string;
  currency: string;
  isActive: boolean;
  lastTransactionAt: string | null;
  createdAt: string;
}

export interface WalletTransaction {
  id: number;
  transactionType: 'credit' | 'debit';
  amount: string;
  balanceAfter: string;
  reason: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface PaymentInitiateResponse {
  paymentId: string;
  gateway: 'razorpay' | 'stripe' | 'paypal';
  gatewayOrderId: string;
  amount: string;
  currency: string;
  keyId?: string;
  clientSecret?: string;
  approvalUrl?: string;
  checkoutUrl?: string;
}

export interface SubscriptionStats {
  totalSubscriptions: number;
  active: number;
  trial: number;
  expired: number;
  monthlyRecurringRevenue: string;
}

export interface PaymentStats {
  totalPayments: number;
  totalRevenueUsd: string;
  totalRevenueInr: string;
  pendingPayments: number;
  completedPayments: number;
  refundedAmount: string;
  activeSubscriptions: number;
  monthlyRecurringRevenue: string;
}

// ─── Subscription Plans ──────────────────────────────────────────────────────

/**
 * GET /payments/subscriptions/plans/
 *
 * List available subscription plans.
 *
 * @param params - Optional ordering and pagination
 * @returns Paginated list of subscription plans
 */
export async function getSubscriptionPlans(params?: {
  ordering?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: SubscriptionPlan[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.ordering) query.set('ordering', params.ordering);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.PAYMENTS.SUBSCRIPTION_PLANS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: SubscriptionPlan[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /payments/subscriptions/plans/by_type/
 *
 * Get plans grouped by type (employer/seafarer).
 *
 * @param type - Plan type filter
 * @returns Paginated list of filtered plans
 */
export async function getSubscriptionPlansByType(
  type: 'employer' | 'seafarer' = 'employer'
): Promise<{
  count: number;
  results: SubscriptionPlan[];
}> {
  try {
    const response = await apiClient.get<{
      count: number;
      results: SubscriptionPlan[];
    }>(`${API_ENDPOINTS.PAYMENTS.SUBSCRIPTION_PLANS_BY_TYPE}?type=${type}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Subscriptions ───────────────────────────────────────────────────────────

/**
 * GET /payments/subscriptions/
 *
 * List subscriptions for the current user.
 * Admins see all subscriptions.
 *
 * @param params - Optional ordering and pagination
 * @returns Paginated list of subscriptions
 */
export async function getSubscriptions(params?: {
  ordering?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: Subscription[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.ordering) query.set('ordering', params.ordering);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.PAYMENTS.SUBSCRIPTIONS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: Subscription[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /payments/subscriptions/
 *
 * Create a new subscription.
 *
 * @param data - Subscription creation data
 * @returns Created subscription
 */
export async function createSubscription(
  data: { planId: number; autoRenew?: boolean }
): Promise<Subscription> {
  try {
    const response = await apiClient.post<Subscription>(
      API_ENDPOINTS.PAYMENTS.SUBSCRIPTIONS,
      { plan_id: data.planId, auto_renew: data.autoRenew ?? true }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /payments/subscriptions/{id}/cancel/
 *
 * Cancel an active subscription.
 *
 * @param id - Subscription ID
 * @returns Cancellation confirmation
 */
export async function cancelSubscription(
  id: number
): Promise<{ detail: string }> {
  try {
    const response = await apiClient.post<{ detail: string }>(
      API_ENDPOINTS.PAYMENTS.SUBSCRIPTION_CANCEL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /payments/subscriptions/my_subscriptions/
 *
 * Get current user's active subscriptions.
 *
 * @param params - Optional pagination
 * @returns Paginated list of active subscriptions
 */
export async function getMySubscriptions(params?: {
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: Subscription[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.PAYMENTS.MY_SUBSCRIPTIONS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: Subscription[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /payments/subscriptions/stats/
 *
 * Admin: Subscription statistics.
 *
 * @returns Subscription stats
 */
export async function getSubscriptionStats(): Promise<SubscriptionStats> {
  try {
    const response = await apiClient.get<SubscriptionStats>(
      `${API_ENDPOINTS.PAYMENTS.SUBSCRIPTIONS}stats`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Payments ────────────────────────────────────────────────────────────────

/**
 * GET /payments/payments/
 *
 * List payments for the current user.
 * Admins see all payments.
 *
 * @param params - Optional search, ordering, and pagination
 * @returns Paginated list of payments
 */
export async function getPayments(params?: {
  search?: string;
  ordering?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: Payment[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.ordering) query.set('ordering', params.ordering);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.PAYMENTS.PAYMENTS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: Payment[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /payments/payments/
 *
 * Create a new payment record.
 *
 * @param data - Payment creation data
 * @returns Created payment
 */
export async function createPayment(data: {
  paymentType: string;
  currency: string;
  amount: string;
  gateway: string;
  description: string;
  subscription?: number;
  dataPayload?: Record<string, unknown>;
}): Promise<Payment> {
  try {
    const payload: Record<string, unknown> = {
      payment_type: data.paymentType,
      currency: data.currency,
      amount: data.amount,
      gateway: data.gateway,
      description: data.description,
    };
    if (data.subscription !== undefined) payload.subscription = data.subscription;
    if (data.dataPayload !== undefined) payload.data_payload = data.dataPayload;

    const response = await apiClient.post<Payment>(
      API_ENDPOINTS.PAYMENTS.PAYMENTS,
      payload
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /payments/payments/{id}/initiate/
 *
 * Initiate payment with gateway.
 *
 * @param id - Payment ID
 * @returns Payment initiation result with gateway URL
 */
export async function initiatePayment(
  id: number
): Promise<PaymentInitiateResponse> {
  try {
    const response = await apiClient.post<PaymentInitiateResponse>(
      API_ENDPOINTS.PAYMENTS.PAYMENT_INITIATE(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /payments/payments/{id}/verify/
 *
 * Verify payment after gateway callback.
 *
 * @param id - Payment ID
 * @param data - Verification data from gateway
 * @returns Verification result
 */
export async function verifyPayment(
  id: number,
  data: Record<string, unknown>
): Promise<{ detail: string }> {
  try {
    const response = await apiClient.post<{ detail: string }>(
      API_ENDPOINTS.PAYMENTS.PAYMENT_VERIFY(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /payments/payments/{id}/refund/
 *
 * Process a refund for a payment.
 *
 * @param id - Payment ID
 * @param data - Refund data
 * @returns Refund result
 */
export async function refundPayment(
  id: number,
  data?: { amount?: number; reason?: string }
): Promise<{
  detail: string;
  refundedAmount: number;
  remainingAmount: number;
}> {
  try {
    const response = await apiClient.post<{
      detail: string;
      refunded_amount: number;
      remaining_amount: number;
    }>(API_ENDPOINTS.PAYMENTS.PAYMENT_REFUND(id), data);
    return {
      detail: response.data.detail,
      refundedAmount: response.data.refunded_amount,
      remainingAmount: response.data.remaining_amount,
    };
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /payments/payments/my_payments/
 *
 * Get current user's payment history.
 *
 * @param params - Optional pagination
 * @returns Paginated list of payments
 */
export async function getMyPayments(params?: {
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: Payment[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.PAYMENTS.MY_PAYMENTS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: Payment[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /payments/payments/dashboard/
 *
 * Admin: Payment dashboard statistics.
 *
 * @param days - Number of days to include (default 30)
 * @returns Payment dashboard stats
 */
export async function getPaymentDashboardStats(
  days: number = 30
): Promise<PaymentStats> {
  try {
    const response = await apiClient.get<PaymentStats>(
      `${API_ENDPOINTS.PAYMENTS.PAYMENTS}dashboard?days=${days}`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Invoices ────────────────────────────────────────────────────────────────

/**
 * GET /payments/invoices/
 *
 * List invoices for the current user.
 * Admins see all invoices.
 *
 * @param params - Optional ordering and pagination
 * @returns Paginated list of invoices
 */
export async function getInvoices(params?: {
  ordering?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: Invoice[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.ordering) query.set('ordering', params.ordering);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.PAYMENTS.INVOICES}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: Invoice[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /payments/invoices/{id}/download/
 *
 * Download invoice PDF.
 * Returns a Blob for direct download handling.
 *
 * @param id - Invoice ID
 * @returns Invoice PDF as a Blob
 */
export async function downloadInvoice(id: number): Promise<Blob> {
  try {
    const response = await apiClient.get<Blob>(
      API_ENDPOINTS.PAYMENTS.INVOICE_DOWNLOAD(id),
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Wallet ──────────────────────────────────────────────────────────────────

/**
 * GET /payments/wallet/my_wallet/
 *
 * Get current user's wallet.
 *
 * @returns Wallet data
 */
export async function getWallet(): Promise<Wallet> {
  try {
    const response = await apiClient.get<Wallet>(
      API_ENDPOINTS.PAYMENTS.WALLET_TRANSACTIONS.replace('transactions/', 'my_wallet/')
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /payments/wallet/transactions/
 *
 * Get wallet transaction history.
 *
 * @param params - Optional pagination
 * @returns Paginated list of wallet transactions
 */
export async function getWalletTransactions(params?: {
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: WalletTransaction[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.PAYMENTS.WALLET_TRANSACTIONS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: WalletTransaction[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}